import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { GamblingCountryConfig } from "src/app/models/common";

@Component({
  selector: "app-gambling-country-datatable",
  templateUrl: "./gambling-country-datatable.component.html",
  styleUrls: ["./gambling-country-datatable.component.scss"],
})
export class GamblingCountryDatatableComponent implements OnInit, OnChanges {

  @Input() config!: GamblingCountryConfig;
  @Output() selectionChange = new EventEmitter<any[]>();

  @ViewChild("gamblingPaginator") gamblingPaginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  gamblingCountrySearch = "";
  gamblingCountriesDataSource = new MatTableDataSource<any>();
  allGamblingCountries: any[] = [];
  selectedGamblingCountries: any[] = [];
  pageLength = 0;
  searchParams = {};

  private pendingSelectedValues: any[] = []; // raw payload values (e.g. "BEL") not yet resolved to full rows

  ngOnInit(): void {
    this.initSelectedCountries();
    this.getCountryData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['config'] || changes['config'].firstChange) return;
    this.resetData();
    this.initSelectedCountries();
    this.getCountryData();
  }

  initSelectedCountries() {
    const incoming = this.config.selectedCountries || [];

    this.selectedGamblingCountries = incoming.filter(c => typeof c === 'object' && c !== null);
    this.pendingSelectedValues = incoming.filter(c => typeof c !== 'object' || c === null);
  }

  getCountryData(startElement = 0, numElements = 243, merge = false) {
    let params: any = { ...this.searchParams, custom: true };

    if (this.config.staticColumns) params.columns = this.config.staticColumns;
    if (this.config.staticTypeParam) params.type = this.config.staticTypeParam;

    this.config.staticService.getStaticData(this.config.staticType, startElement, numElements, params).subscribe((res) => {
      const countries = res[this.config.responseKey] || [];
      const total = res[this.config.countKey] || 0;

      // Resolve any pending bare payload values (e.g. "BEL") against this batch of country data.
      if (this.pendingSelectedValues.length) {
        const resolved = countries.filter(c => this.pendingSelectedValues.includes(c[this.config.payloadField]));
        if (resolved.length) {
          this.selectedGamblingCountries.push(...resolved.map(c => ({ ...c, selected: false })));
          this.pendingSelectedValues = this.pendingSelectedValues.filter(
            v => !resolved.some(c => c[this.config.payloadField] === v)
          );
        }
      }

      const data = countries
        .filter(country => !this.selectedGamblingCountries.some(selected => selected[this.config.displayField] === country[this.config.displayField]))
        .map(country => ({ ...country, selected: false }));

      this.allGamblingCountries = merge ? [...this.allGamblingCountries, ...data] : data;

      this.gamblingCountriesDataSource.data = this.allGamblingCountries;
      this.pageLength = total;
      this.gamblingCountriesDataSource.paginator = this.gamblingPaginator;
      this.gamblingCountriesDataSource.sort = this.sort;
    });
  }

  searchGamblingCountries() {
    this.searchParams = this.config.searchField === "search_term"
      ? { search_term: this.gamblingCountrySearch }
      : { fields: this.config.searchField, [this.config.searchField]: this.gamblingCountrySearch };

    this.allGamblingCountries = [];
    this.getCountryData();
  }

  selectGamblingCountry(country: any) {
    this.selectedGamblingCountries.push(country);
    this.allGamblingCountries = this.allGamblingCountries.filter(c => c[this.config.displayField] !== country[this.config.displayField]);
    this.gamblingCountriesDataSource.data = this.allGamblingCountries;
    this.syncSelectedCountries();
  }

  removeGamblingCountry(country: any) {
    this.selectedGamblingCountries = this.selectedGamblingCountries.filter(c => c !== country);
    this.allGamblingCountries.push({ ...country, selected: false });
    this.gamblingCountriesDataSource.data = this.allGamblingCountries;
    this.syncSelectedCountries();
  }

  removeAllGamblingCountries() {
    this.allGamblingCountries.push(...this.selectedGamblingCountries.map(c => ({ ...c, selected: false })));
    this.selectedGamblingCountries = [];
    this.gamblingCountriesDataSource.data = this.allGamblingCountries;
    this.syncSelectedCountries();
  }

  handleCountryPage(event: PageEvent) {
    const requestedPage = event.pageSize * (event.pageIndex + 1);
    if (requestedPage > this.allGamblingCountries.length) this.getCountryData(this.allGamblingCountries.length, 100, true);
  }

  resetData() {
    this.gamblingCountrySearch = '';
    this.searchParams = {};

    this.allGamblingCountries = [];
    this.selectedGamblingCountries = [];
    this.gamblingCountriesDataSource.data = [];

    this.gamblingPaginator?.firstPage();
  }

  private syncSelectedCountries() {
    const values = this.selectedGamblingCountries.map(c => c[this.config.payloadField]);
    this.selectionChange.emit(values);
  }
}